import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { deleteField, doc, setDoc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

import { auth, db, storage } from '../../../firebase';

import placeholder_profile from '../../../assets/images/profile/placeholder-profile.jpg';
import upload_icon from '../../../assets/images/icons/upload-icon.png';

import './generalSettings.css';
import '../../UserSettings.css';

const languages = [
  { value: 'en', label: 'English (English)' },
  { value: 'hi', label: 'Hindi (हिन्दी)' },
  { value: 'es', label: 'Spanish (Español)' },
  { value: 'pt', label: 'Portuguese (Português)' },
  { value: 'ru', label: 'Russian (Русский)' },
  { value: 'de', label: 'German (Deutsch)' },
  { value: 'fr', label: 'French (Français)' },
  { value: 'ja', label: 'Japanese (日本語)' },
  { value: 'ko', label: 'Korean (한국어)' },
  { value: 'ar', label: 'Arabic (العربية)' },
  { value: 'he', label: 'Hebrew (עברית)' }
];


const GeneralSettings = () => {
  const user = auth.currentUser;
  const [profileImage, setProfileImage] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [nameDraft, setNameDraft] = useState(null); // non-null while editing
  const [nameSaved, setNameSaved] = useState(false);
  const nameCancelled = useRef(false); // Escape: the blur that follows must not save
  const [savedLanguage, setSavedLanguage] = useState('en');
  const [language, setLanguage] = useState('en');
  const [isSaving, setIsSaving] = useState(false);
  const [languageSaved, setLanguageSaved] = useState(false);
  const [currentPlan, setCurrentPlan] = useState('');
  const fileInputRef = useRef(null);

  // Live profile (avatar, name, AI language) and plan
  useEffect(() => {
    if (!user) return;
    const unsubProfile = onSnapshot(doc(db, 'users', user.uid, 'userSettings', 'profile'), snap => {
      const data = snap.data() || {};
      setProfileImage(data.profileImage || '');
      setDisplayName(data.displayName || '');
      if (data.userDefaultLanguage) {
        setSavedLanguage(data.userDefaultLanguage);
        setLanguage(data.userDefaultLanguage);
      }
    }, () => {});
    const unsubPlan = onSnapshot(doc(db, 'users', user.uid, 'subscriptionData', 'planDetails'), snap => {
      setCurrentPlan((snap.data()?.currentPlan || 'free').toLowerCase());
    }, () => {});
    return () => { unsubProfile(); unsubPlan(); };
  }, [user]);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (file.size >= 1024 * 1024) {
      alert('File is too large. Please select a file less than 1MB.');
      return;
    }
    try {
      const storageRef = ref(storage, `profileImages/${user.uid}`);
      await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(storageRef);
      await setDoc(doc(db, 'users', user.uid, 'userSettings', 'profile'), { profileImage: photoURL }, { merge: true });
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  // Name: optional, saves on Enter / click away like a workspace title; Escape undoes the edit.
  // Clearing it removes the field (the sidebar then shows the email).
  const commitName = async () => {
    const next = (nameDraft ?? '').trim().replace(/\s+/g, ' ');
    setNameDraft(null);
    if (nameCancelled.current) { nameCancelled.current = false; return; }
    if (next === displayName) return;
    const previous = displayName;
    setDisplayName(next);
    try {
      await setDoc(doc(db, 'users', user.uid, 'userSettings', 'profile'), { displayName: next || deleteField() }, { merge: true });
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 1500);
    } catch (error) {
      console.error('Error saving name:', error);
      setDisplayName(previous);
    }
  };

  const saveLanguageSetting = async () => {
    setIsSaving(true);
    try {
      await setDoc(doc(db, 'users', user.uid, 'userSettings', 'profile'), { userDefaultLanguage: language }, { merge: true });
      setLanguageSaved(true);
      setTimeout(() => setLanguageSaved(false), 3000);
    } catch (error) {
      console.error('Error saving language:', error);
    }
    setIsSaving(false);
  };

  const planName = currentPlan === 'pro' ? 'Pro Plan' : currentPlan === 'free' ? 'Free Plan' : currentPlan;

  return (
    <>
      <div className="setting-header">General</div>
      <div className="account-settings-container">
        {/* Avatar Row */}
        <div className="settings-row">
          <div className="settings-label">Avatar</div>
          <div className="settings-field">
            <div className="avatar-container">
              <img src={profileImage || placeholder_profile} alt="User Avatar" className="avatar-image" />
              <button className="avatar-upload-button" onClick={() => fileInputRef.current.click()}>
                <img src={upload_icon} alt="Upload" />
              </button>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              accept=".png, .jpg, .jpeg"
            />
          </div>
        </div>

        {/* Email Row */}
        <div className="settings-row">
          <div className="settings-label">Email</div>
          <div className="settings-field">
            <span>{user?.email}</span>
          </div>
        </div>

        {/* Name Row */}
        <div className="settings-row">
          <div className="settings-label">
            Name
            <div className="settings-subtitle">Optional. Shown in the sidebar instead of your email.</div>
          </div>
          <div className="settings-field">
            {nameSaved && <FontAwesomeIcon icon={faCheck} className="name-saved" aria-label="Saved" />}
            <input
              type="text"
              className="name-input"
              value={nameDraft ?? displayName}
              onFocus={() => setNameDraft(displayName)}
              onChange={e => setNameDraft(e.target.value)}
              onBlur={commitName}
              onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') { nameCancelled.current = true; e.currentTarget.blur(); }
              }}
              placeholder="Add your name"
              maxLength={50}
              autoComplete="name"
              aria-label="Name"
            />
          </div>
        </div>

        {/* Plan Row */}
        <div className="settings-row">
          <div className="settings-label">Subscription</div>
              <div className="settings-field">
                {planName && <span className="settings-plan-pill">{planName}</span>}
                <Link to="/settings/plan" className="ui-setting-button">View</Link>
              </div>
        </div>


        {/* Language Row */}
        <div className="settings-row">
          <div className="settings-label">
            AI Output Language
            <div className="settings-subtitle">The language used for AI responses. For local uploads, match the selected language to your video’s language for accuracy.</div>
          </div>
          <div className="language-settings-field">
            <div className="select-wrapper">
              <select className="language-dropdown" value={language} onChange={e => setLanguage(e.target.value)}>
                {languages.map((lang) => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="ui-setting-button ui-setting-button--primary"
              onClick={saveLanguageSetting}
              disabled={isSaving || language === savedLanguage}
            >
              {languageSaved ? 'Saved' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default GeneralSettings;
